import { useCallback, useLayoutEffect, useState } from 'react'
import { useForm, useWatch } from 'react-hook-form'
import { useDispatch } from 'react-redux'
import { useHistory, useParams } from 'react-router-dom'
import { Child, getChild } from '../common/child'
import { getCategories } from '../common/category'
import { useErrorHandle } from '../common/error/errorHandler'
import { showLoading, notifyMessage } from '../common/store/slices/application'
import { useOperationLog } from '../common/operationLog'
import { OperationId } from '../common/constant/operationLog'
import { Flag, yesNo } from '../common/constant/classification'
import { memberUrl } from '../common/constant/appUrl'
import { tabValue } from '../member/memberService'
import { translate } from '../../i18n'
import { undefinedPropsToNull } from '../../utils/objectUtil'
import { GetCategoryMstDto } from '../../dataAccess/webApi/dto/categoryMstDto'
import { GetChildrenFebrileSeizureDto } from '../../dataAccess/webApi/dto/childrenFebrileSeizureDto'
import { getHasUsagePermission  } from '../common/category'
import {
  executeGetChildrenFebrileSeizure,
  executePutChildrenFebrileSeizure
} from '../../dataAccess/webApi/dao/childrenFebrileSeizureDao'

/** カテゴリーキー */
const CATEGORY_KEY = 'febrileSeizure'
/** サブカテゴリーキー */
const SUB_CATEGORIES = [
  'febrileSeizure.firstSeizureAge',
  'febrileSeizure.lastSeizureAge',
  'febrileSeizure.count',
  'febrileSeizure.useMedicine',
  'febrileSeizure.noteUser',
]
/** 存在フラグ名 */
const EXIST_FLAG_NAME = 'febrileSeizureFlag'
/** 市民用事業ID（市民用の項目は事業設ID'0'） */
const DEFAULT_PROJECT_ID = '0'
/** 市民用施設ID（市民用のデータは施設ID'0'で作成する） */
const DEFAULT_FACILITY_ID = '0'

/** URLパラメータ */
interface UrlParams {
  childId: string
}

/** 熱性けいれんの入力フォームの項目の定義 */
export interface Inputs {
  febrileSeizureFlag: string | null
  febrileSeizure?: {
    firstSeizureAge?: number | null
    lastSeizureAge?: number | null
    count?: number | null
    useMedicine?: string | null
    noteUser?: string | null
  }
}

/** 入力フォームの初期値 */
const FORM_DEFAULT_VALUES: Inputs = {
  febrileSeizureFlag: Flag.OFF,
}

export const useAction = () => {
  const errorHandle = useErrorHandle()
  const history = useHistory()
  const dispatch = useDispatch()

  // URLパラメータ取得
  const { childId } = useParams<UrlParams>()
  // カテゴリーマスタ
  const [categoryMst, setCategoryMst] = useState<GetCategoryMstDto[]>()
  // お子さま詳細更新日時
  const [detailUpdateDatetime, setDetailUpdateDatetime] = useState<string>()
  // 熱性けいれん情報更新日時
  const [febrileSeizureUpdateDatetime, setFebrileSeizureUpdateDatetime] = useState<string>()
  // リザルトコード
  const [childrenResultCode, setChildrenResultCode] = useState<number>()
  // 利用許可
  const [hasUsagePermission, setHasUsagePermission] = useState<boolean>(false)  
  // オペレーションログ
  const { addOperationLog } = useOperationLog()

  // 入力フォーム初期値設定
  const formMethods = useForm<Inputs>({
    defaultValues: FORM_DEFAULT_VALUES
  })

  // 入力値監視（なしあり）
  const existFlag = useWatch({ name: EXIST_FLAG_NAME, control: formMethods.control })
  
  // 初期処理（ちらつき防止のためにuseLayoutEffect使用）
  useLayoutEffect(() => {
    addOperationLog({
      operationId: OperationId.OP_00000001,
      accessData: [{ userIdRegFlag: yesNo.yes, childId }]
    })
    
    dispatch(
      showLoading(
        errorHandle(async () => {
          const [
            category, 
            child,
            hasUsagePermission,
            febrileSeizureResp
          ] = await Promise.all([
            // カテゴリーマスタ取得
            getCategories({projectId: DEFAULT_PROJECT_ID, categories: [CATEGORY_KEY, ...SUB_CATEGORIES]}),
            // お子さま情報取得
            getChild(childId, DEFAULT_FACILITY_ID),
            // 利用許可
            getHasUsagePermission(childId),
            // 熱性けいれん情報取得
            executeGetChildrenFebrileSeizure(childId, DEFAULT_FACILITY_ID)
          ])

          // カテゴリーマスタ設定
          setCategoryMst(category)
          // お子さま情報設定
          setDetailUpdateDatetime(child.detailUpdateDatetime ?? '')
          // 利用許可 
          setHasUsagePermission(hasUsagePermission)
          // 熱性けいれん情報更新日時
          setFebrileSeizureUpdateDatetime(febrileSeizureResp?.result[0]?.updateDatetime ?? '')
          // 入力フォームに設定
          formMethods.reset({
            ...formMethods.getValues(),
            ...getInitialInputs(child, febrileSeizureResp.result),
          })
        })
      )
    )

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // 確定処理
  const onSubmit = useCallback(
    (data: Inputs) => {
      addOperationLog({ operationId: OperationId.OP_00000037 })

      if (!validation(data, formMethods)) {
        // バリデーションチェックエラー
        return
      }

      dispatch(
        showLoading({
          process: errorHandle(async () => {
            // 登録情報を設定
            const regData = getRegistFebrileSeizure(data, detailUpdateDatetime, febrileSeizureUpdateDatetime)
            // 登録実行
            const resp = await executePutChildrenFebrileSeizure(childId, regData)
            if (resp.resultCode) {
              // 失敗時
              setChildrenResultCode(resp.resultCode)
            } else {
              // 成功時
              dispatch(notifyMessage(translate('memberModifyChildFebrileSeizure.success.completionMessage')))
              history.push(memberUrl.url(), { tabValue: tabValue.child, childId })
            }
          }),
          isHiddenMain: false,
        })
      )
    },
    [addOperationLog, detailUpdateDatetime, febrileSeizureUpdateDatetime]
  )

  // 情報返却
  return {
    CATEGORY_KEY,
    SUB_CATEGORIES,
    EXIST_FLAG_NAME,
    formMethods,
    existFlag,
    categoryMst,
    hasUsagePermission,
    onSubmit,
    childrenResultCode,
  }
}

/**
 * 入力フォームの初期値設定
 */
const getInitialInputs = (
  child: Child,
  childrenFebrileSeizure: GetChildrenFebrileSeizureDto[]
) => {
  const inputs = {
    febrileSeizureFlag: child.febrileSeizureFlag ?? Flag.OFF,
    febrileSeizure: {
      ...childrenFebrileSeizure[0]
    }
  }

  return inputs
}

/**
 * バリデーションチェック
 */
const validation = (data: Inputs, formMethods: any) => {
  let result = true

  // なしの場合はチェックしない
  if (data.febrileSeizureFlag !== Flag.ON) {
    return result
  }

  if (data.febrileSeizure) {
    const { firstSeizureAge, lastSeizureAge, count } = data.febrileSeizure

    // 数値チェック（inputタイプをnumberにするとmaxlengthが効かなかったのでこちらでチェック）
    if (firstSeizureAge && !String(firstSeizureAge).match(/^[0-9]*$/)) {
      formMethods.setError(`febrileSeizure.firstSeizureAge`, {
        message: translate('system.error.onlyHalfWidthNumbers')
      })
      result = false
    }
    if (lastSeizureAge && !String(lastSeizureAge).match(/^[0-9]*$/)) {
      formMethods.setError(`febrileSeizure.lastSeizureAge`, {
        message: translate('system.error.onlyHalfWidthNumbers')
      })
      result = false
    }
    if (count && !String(count).match(/^[0-9]*$/)) {
      formMethods.setError(`febrileSeizure.count`, {
        message: translate('system.error.onlyHalfWidthNumbers')
      })
      result = false
    }
  }
  
  return result
}

/**
 * お子さま情報登録APIのINPUT用の熱性けいれん情報を取得
 * @param data 入力値
 * @param detailUpdateDatetime 更新前更新日時
 * @returns お子さま情報登録APIのINPUT用の熱性けいれん情報
 */
const getRegistFebrileSeizure = (data: Inputs, detailUpdateDatetime?: string, febrileSeizureUpdateDatetime?: string) => {
  // 共通情報
  const commonInfo = {
    facilityId: DEFAULT_FACILITY_ID,
    detailUpdateDatetime: detailUpdateDatetime ?? '',
  }

  // 熱性けいれん情報
  if (data.febrileSeizureFlag !== Flag.ON) {
    // なし
    return undefinedPropsToNull({
      ...commonInfo,
      febrileSeizureFlag: data.febrileSeizureFlag ?? Flag.OFF,
    })
  } else {
    // あり
    const { febrileSeizureFlag, febrileSeizure } = data

    // 熱性けいれん情報編集
    let regFebrileSeizure
    if (febrileSeizure) {
      const { firstSeizureAge, lastSeizureAge, count, ...obj } = febrileSeizure
      regFebrileSeizure = {
        updateDatetime : febrileSeizureUpdateDatetime ? '' : null,
        firstSeizureAge: firstSeizureAge ? Number(firstSeizureAge) : null,
        lastSeizureAge: lastSeizureAge ? Number(lastSeizureAge) : null,
        count: count ? Number(count) : null,
        ...obj,
      }
    }

    return undefinedPropsToNull({
      ...commonInfo,
      febrileSeizureFlag: febrileSeizureFlag ?? Flag.OFF,
      febrileSeizure: regFebrileSeizure
    })
  }
}